const React = require('react');
const { reloadPage } = require('utils/reload');
const { isBundleLoadError } = require('utils/bundle-load-error');
const ClientErrorReporting = require('services/client-error-reporting');

const {
    claimRouteErrorRecovery,
    reportRouteErrorRecovery,
    shouldSimulateRouteError
} = require('utils/route-error-recovery');

const {
    Component,
    Suspense,
    lazy
} = React;

const FallbackSpinner = require('./fallback-spinner');

const internals = {};

module.exports = function loadableRoute(loader, options = {}) {

    const {
        fallback = <FallbackSpinner />,
        errorFallback: ErrorFallback = internals.RouteChunkErrorFallback
    } = options;

    const LazyComponent = lazy(() => loader().then((module) => ({
        default: module.default || module
    })));

    return function LoadableRouteComponent(props) {

        const {
            RouteChunkErrorBoundary,
            DebugRouteError
        } = internals;

        return (
            <RouteChunkErrorBoundary fallback={ErrorFallback}>
                <Suspense fallback={fallback}>
                    <DebugRouteError>
                        <LazyComponent {...props} />
                    </DebugRouteError>
                </Suspense>
            </RouteChunkErrorBoundary>
        );
    };
};

internals.DebugRouteError = function DebugRouteError({ children }) {

    if (shouldSimulateRouteError()) {
        throw new TypeError("Cannot read properties of undefined (reading 'simulatedRouteDependency')");
    }

    return children;
};

internals.RouteChunkErrorBoundary = class RouteChunkErrorBoundary extends Component {

    state = {
        error: null
    }

    static getDerivedStateFromError(error) {

        return { error };
    }

    componentDidCatch(error, errorInfo) {

        // eslint-disable-next-line no-console
        console.error('Lazy route failed to load', error, errorInfo);

        if (!claimRouteErrorRecovery({ error })) {
            ClientErrorReporting.reportClientError({
                error,
                errorInfo,
                source: 'react.route-boundary',
                type: 'react_error_boundary'
            }).catch(() => {});
            return;
        }

        internals.reportBeforeReload({ error, errorInfo });
    }

    render() {

        const { error } = this.state;
        const {
            children,
            fallback: Fallback
        } = this.props;

        if (!error) {
            return children;
        }

        return <Fallback error={error} />;
    }
};

internals.reportBeforeReload = async ({ error, errorInfo }) => {

    try {
        await reportRouteErrorRecovery({ error, errorInfo });
    }
    catch (err) {
        // Recovery must still proceed when telemetry is unavailable.
    }

    reloadPage();
};

internals.RouteChunkErrorFallback = function RouteChunkErrorFallback({ error }) {

    const description = isBundleLoadError(error)
        ? 'Part of this page did not load correctly. Please check your connection and try again.'
        : 'Something went wrong while opening this page. Please try again.';

    return (
        <div style={internals.styles.errorContainer} role='alert'>
            <h2 style={internals.styles.title}>We couldn&rsquo;t open this page.</h2>
            <p style={internals.styles.message}>{description}</p>
            <button
                type='button'
                style={internals.styles.button}
                onClick={reloadPage}
            >
                Try again
            </button>
        </div>
    );
};

internals.styles = {
    errorContainer: {
        padding: 24,
        margin: '32px auto',
        maxWidth: 560,
        border: '1px solid rgba(70, 17, 169, .18)',
        borderRadius: 10,
        background: '#fff',
        color: '#2b2140',
        textAlign: 'center'
    },
    title: {
        margin: '0 0 8px',
        fontSize: 20
    },
    message: {
        margin: '0 0 12px',
        lineHeight: 1.4
    },
    button: {
        border: 0,
        borderRadius: 999,
        background: '#4611a9',
        color: '#fff',
        cursor: 'pointer',
        fontWeight: 700,
        padding: '8px 16px'
    }
};

if (process.env.NODE_ENV === 'test') {
    module.exports._internals = internals;
}
